import { useRef } from 'react';
import { Box } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useSnackbar } from 'notistack';

import OtpField from 'src/components/OtpField';
import SendCodeAgain from 'src/widgets/SendCodeAgain';
import SessionService from 'src/services/SessionService';
import useUserStore from 'src/stores/user';
import useSessionStore from 'src/stores/session';
import { BEGIN_WORK_STEPS_IDS } from 'src/constants/beginWorksSteps';
import { updateBeginWorkStep } from 'src/providers/UpdateBeginWorkStepsProvider';
import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import useGoTo from 'src/hooks/useGoTo';

type ApproveEmailForm = {
    code: string;
};

export default function ApproveEmail() {
    const { enqueueSnackbar } = useSnackbar();
    const ref = useRef<HTMLButtonElement | null>(null);

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<ApproveEmailForm>({
        defaultValues: {
            code: '',
        },
    });

    const { mutateAsync: refetchEmailCode, isLoading: isRefetchEmailCodeLoading } = useMutation(
        SessionService.queryKey,
        useUserStore.getState().refetchEmailCode,
        {
            onSuccess: () => {
                enqueueSnackbar('Код отправлен на вашу почту', { variant: 'success' });
                reset();
            },
            onError: (error) => {
                console.info(error);
                enqueueSnackbar('Ошибка отправки кода', { variant: 'error' });
            },
        }
    );

    const goTo = useGoTo();
    const { checkUserDataChanges } = useSessionStore(({ checkUserDataChanges }) => ({
        checkUserDataChanges,
    }));

    // Флаг для предотвращения двойных уведомлений
    const notificationShownRef = useRef(false);

    const { mutate: confirmEmail, isLoading: confirmEmailLoading } = useMutation(
        SessionService.queryKey,
        useUserStore.getState().confirmEmail,
        {
            onSuccess: async () => {
                // Устанавливаем флаг СРАЗУ, чтобы предотвратить показ ошибки в onError (race condition)
                notificationShownRef.current = true;

                // Обновляем данные пользователя ПЕРЕД показом уведомления
                // Это предотвращает race condition и двойные уведомления
                try {
                    await checkUserDataChanges();
                } catch (error) {
                    console.warn('Предупреждение при обновлении данных пользователя:', error);
                    // Не показываем ошибку, т.к. основная операция (подтверждение почты) успешна
                }

                // Показываем уведомление об успехе (флаг уже установлен, поэтому onError не покажет ошибку)
                const userState = useUserStore.getState();
                if (userState.isEmailConfirmed) {
                    enqueueSnackbar('Почта подтверждена!', { variant: 'success' });
                } else {
                    // Если по какой-то причине статус не обновился, все равно показываем успех
                    // (возможно, данные еще не синхронизировались)
                    console.warn('Почта подтверждена, но статус не обновился в store');
                    enqueueSnackbar('Почта подтверждена!', { variant: 'success' });
                }

                // Обновляем шаг онбординга БЕЗ обработки ошибок (если шаг уже обновлен - это нормально)
                // Используем setTimeout чтобы не блокировать UI и обработать возможные ошибки тихо
                setTimeout(async () => {
                    try {
                        await updateBeginWorkStep({ currentStep: BEGIN_WORK_STEPS_IDS.EMAIL_CONFIRM });
                    } catch (error: any) {
                        // Игнорируем ошибки обновления шага - они не критичны после подтверждения почты
                        // Шаг может быть уже обновлен на бэкенде
                        const errorMessage = error?.response?.data?.error || error?.message || '';
                        if (errorMessage.includes('already') || errorMessage.includes('Cannot skip')) {
                            console.log('Шаг онбординга уже обновлен, это нормально');
                        } else {
                            console.warn('Предупреждение при обновлении шага онбординга:', errorMessage);
                        }
                    }
                }, 100);
            },
            onError: async (error: any) => {
                console.info(error);

                // Если уже показали успешное уведомление (onSuccess сработал первым) - не показываем ошибку
                if (notificationShownRef.current) {
                    console.log('Успешное уведомление уже показано, пропускаем ошибку');
                    return;
                }

                // Проверяем, не была ли почта уже подтверждена (может быть race condition)
                const errorMessage =
                    error?.response?.data?.code?.[0] || error?.response?.data?.error || error?.message || '';
                const isAlreadyConfirmed =
                    errorMessage.includes('уже подтвержден') ||
                    errorMessage.includes('already confirmed') ||
                    errorMessage.includes('already confirmed');

                // Обновляем данные пользователя для проверки актуального статуса
                try {
                    await checkUserDataChanges();
                } catch (e) {
                    console.warn('Предупреждение при обновлении данных:', e);
                }

                // Проверяем текущий статус пользователя ПОСЛЕ обновления данных
                const userState = useUserStore.getState();
                const isActuallyConfirmed = userState.isEmailConfirmed;

                // Устанавливаем флаг, чтобы предотвратить повторные уведомления
                notificationShownRef.current = true;

                if (isAlreadyConfirmed || isActuallyConfirmed) {
                    // Если почта уже подтверждена, показываем информационное сообщение (не ошибку)
                    enqueueSnackbar('Почта уже подтверждена', { variant: 'info' });
                } else {
                    // Реальная ошибка - показываем только если еще не показывали уведомление
                    enqueueSnackbar('Ошибка подтверждения почты', { variant: 'error' });
                }
            },
        }
    );

    const onSubmit = ({ code }: ApproveEmailForm) => {
        confirmEmail(code);
    };

    const handleComplete = () => {
        if (ref.current) ref.current.click();
    };

    const handleRefetchCode = async () => {
        return refetchEmailCode();
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <LoadingWrapper
                isLoading={confirmEmailLoading || isRefetchEmailCodeLoading}
                isLocal
            >
                <Box mb={2}>
                    <Controller
                        name="code"
                        control={control}
                        render={({ field }) => (
                            <OtpField
                                {...field}
                                error={errors?.code?.message}
                                onComplete={handleComplete}
                            />
                        )}
                    />
                </Box>
                <Box mb={0.5}>
                    <SendCodeAgain
                        timer={30}
                        disabled={confirmEmailLoading}
                        onClick={handleRefetchCode}
                    />
                </Box>
                <Box display="none">
                    <button
                        type="submit"
                        ref={(r) => (ref.current = r)}
                    ></button>
                </Box>
            </LoadingWrapper>
        </form>
    );
}
